/**
 * Typed-date parsing for the shared DateField.
 *
 * The browser's native date input edits a date one segment at a time and lets the
 * year segment take up to six digits, so a slip while typing "2026" produced
 * 0206-11-02, which was saved and posted (F-LR-020). This parser takes the whole
 * string the person typed, accepts the ways people write dates, and refuses a
 * year outside a sane window instead of guessing.
 *
 * Values in and out are ISO calendar dates (YYYY-MM-DD), the same contract as
 * <input type="date">, so a screen swaps one for the other without other changes.
 */

export const MIN_YEAR = 1900;
export const MAX_YEAR = 2199;

const MONTHS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11,
  dec: 12, december: 12,
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export type DateParse =
  | { kind: 'empty' }
  | { kind: 'valid'; iso: string }
  | { kind: 'invalid'; reason: string };

const pad = (n: number) => String(n).padStart(2, '0');

export const toIso = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`;

export const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

const expandYear = (raw: string): number | null => {
  if (/^\d{4}$/.test(raw)) return Number(raw);
  if (/^\d{2}$/.test(raw)) return 2000 + Number(raw);
  return null;
};

const build = (year: number | null, month: number, day: number): DateParse => {
  if (year === null) return { kind: 'invalid', reason: 'Use a four-digit year' };
  if (year < MIN_YEAR || year > MAX_YEAR) {
    return { kind: 'invalid', reason: `The year ${year} is not plausible; check it` };
  }
  if (month < 1 || month > 12) return { kind: 'invalid', reason: 'There is no month ' + month };
  if (day < 1 || day > daysInMonth(year, month)) {
    return { kind: 'invalid', reason: `${MONTH_SHORT[month - 1]} ${year} has no day ${day}` };
  }
  return { kind: 'valid', iso: toIso(year, month, day) };
};

/**
 * Accepts: 11/02/2026 · 11/2/26 · 11-02-2026 · 11.02.2026 (month first, the US
 * convention the platform uses) · 2026-11-02 · 2026/11/02 · 11022026 · 20261102 ·
 * 2 Nov 2026 · Nov 2 2026 · November 2, 2026 · 2-Nov-2026.
 */
export const parseTypedDate = (input: string): DateParse => {
  const text = input.trim();
  if (!text) return { kind: 'empty' };

  let m = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) return build(Number(m[1]), Number(m[2]), Number(m[3]));

  m = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (m) return build(expandYear(m[3]), Number(m[1]), Number(m[2]));

  m = text.match(/^(\d{8})$/);
  if (m) {
    const digits = m[1];
    if (/^(19|20|21)\d{2}(0[1-9]|1[0-2])/.test(digits)) {
      return build(Number(digits.slice(0, 4)), Number(digits.slice(4, 6)), Number(digits.slice(6, 8)));
    }
    return build(Number(digits.slice(4, 8)), Number(digits.slice(0, 2)), Number(digits.slice(2, 4)));
  }

  const words = text.toLowerCase().replace(/,/g, ' ').replace(/-/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 3) {
    const [a, b, c] = words;
    if (MONTHS[b] && /^\d{1,2}$/.test(a)) return build(expandYear(c), MONTHS[b], Number(a));
    if (MONTHS[a] && /^\d{1,2}$/.test(b)) return build(expandYear(c), MONTHS[a], Number(b));
  }

  if (/^\d{1,2}[-/.]\d{1,2}[-/.]\d+$/.test(text) || /^\d+[-/.]\d{1,2}[-/.]\d{1,2}$/.test(text)) {
    return { kind: 'invalid', reason: 'Use a four-digit year' };
  }
  return { kind: 'invalid', reason: 'Not a date. Type it as 11/02/2026' };
};

const parts = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) } : null;
};

/** 2026-11-02 → 11/02/2026, the form the field shows while not being edited. */
export const formatForField = (iso: string): string => {
  const p = parts(iso);
  return p ? `${pad(p.month)}/${pad(p.day)}/${p.year}` : '';
};

/** 2026-11-02 → "Mon 2 Nov 2026": the weekday and month name catch a wrong digit at a glance. */
export const describeDate = (iso: string): string => {
  const p = parts(iso);
  if (!p) return '';
  const weekday = new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
  return `${WEEKDAY_SHORT[weekday]} ${p.day} ${MONTH_SHORT[p.month - 1]} ${p.year}`;
};

/** ISO strings compare correctly as text; empty bounds are ignored. */
export const outOfBounds = (iso: string, min?: string, max?: string): 'before' | 'after' | null => {
  if (min && iso < min) return 'before';
  if (max && iso > max) return 'after';
  return null;
};

export const monthLabel = (year: number, month: number) => `${MONTH_SHORT[month - 1]} ${year}`;

export const isoParts = parts;
